import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography, Box, Select, Button } from '@mui/material';
import {
  NEW_AUDIO_STATUS,
  NEW_AUDIO_STATUSES,
} from '@src/constants/voiceCloning';
import { getAudioStatus } from '@src/services/voiceCloning';
import FirstVoiceBoxImg from '@src/assets/images/create-first-voice-box.svg';
import { CheckRounded, CloseRounded } from '@mui/icons-material';
import { StyledGridSentenceButton, StyledMenuItem } from './index.style';

const GridSentenceButton = ({
  open,
  onClose,
  audiosArray,
  currentSentenceIndex,
  onSelectSentence,
  onNextCheckInformation,
}) => {
  const { t } = useTranslation();
  const { voice } = useSelector((state) => state.voiceVoiceCloning);
  const alreadyRecorded = voice?.submitAt;

  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState('allSentence');
  const [filteredAudios, setFilteredAudios] = useState([]);

  const isSentenceButtonDisabled = (index, audio) =>
    !audio.audioLink && index !== currentSentenceIndex;

  const renderStyleSentenceBtn = ({ status, quality, audioLink }) => {
    if (status === NEW_AUDIO_STATUS.FAILED) return 'failed';
    if (!audioLink) return 'disabled';

    const isPassedQuality = Object.values(quality).every(Boolean);
    if (isPassedQuality) return 'passed';

    return 'failed';
  };

  const handleStatusFilterChange = (e) =>
    setSelectedStatusFilter(e.target.value);

  const mapAudioWithIndex = (audio, index) => ({
    ...audio,
    originalIndex: index,
  });

  const filterAudio = (audio) =>
    !(
      alreadyRecorded &&
      (audio.status === NEW_AUDIO_STATUS.PASSED ||
        audio.status === NEW_AUDIO_STATUS.GOOD ||
        (audio.status === NEW_AUDIO_STATUS.PENDING && !audio?.note))
    );

  const setAudiosToShow = () => {
    const mappedAudios = audiosArray.map(mapAudioWithIndex);
    const audios =
      selectedStatusFilter === 'allSentence'
        ? mappedAudios.filter(filterAudio)
        : mappedAudios.filter(
            (audio) =>
              filterAudio(audio) && audio.status === selectedStatusFilter,
          );

    setFilteredAudios(audios);
  };

  const allSentencesRecored = useMemo(
    () => audiosArray.every((audio) => audio.audioLink),
    [audiosArray],
  );

  useEffect(() => {
    setAudiosToShow();
  }, [selectedStatusFilter, audiosArray]);

  return (
    <StyledGridSentenceButton open={open} onClose={onClose}>
      <Box className="box-dialog">
        <Box className="box-grid-title">
          <Typography className="grid-title">{t('sentenceList')}</Typography>
          <Button onClick={onClose} className="button-close">
            <CloseRounded />
          </Button>
        </Box>
        {!alreadyRecorded && (
          <Box className="box-sentence-select">
            <Select
              value={selectedStatusFilter}
              onChange={handleStatusFilterChange}
              className="status-select"
              renderValue={(selected) => <>{t(getAudioStatus(selected))}</>}
            >
              {NEW_AUDIO_STATUSES.map((item) => (
                <StyledMenuItem
                  key={item.label}
                  value={item.value}
                  label={item.label}
                >
                  {t(getAudioStatus(item.label))}
                  {selectedStatusFilter === item.value && (
                    <CheckRounded className="check-icon" />
                  )}
                </StyledMenuItem>
              ))}
            </Select>
          </Box>
        )}
        {filteredAudios.length === 0 ? (
          <img
            src={FirstVoiceBoxImg}
            alt="empty-list"
            className="image-empty"
          />
        ) : (
          <Box className="box-sentence">
            {filteredAudios.map((audio) => (
              <Button
                className={`sentence-btn ${renderStyleSentenceBtn({
                  status: audio?.status,
                  quality: audio?.quality,
                  audioLink: audio?.audioLink,
                })} ${
                  audio.originalIndex === currentSentenceIndex &&
                  'current-sentence'
                }`}
                key={audio.id}
                onClick={() => {
                  onSelectSentence(audio.originalIndex);
                  onClose();
                }}
                disabled={isSentenceButtonDisabled(audio.originalIndex, audio)}
              >
                {audio.originalIndex + 1}
              </Button>
            ))}
          </Box>
        )}
        <Box className="box-send">
          {allSentencesRecored ? (
            <Button
              className="action-button pending-button"
              onClick={onNextCheckInformation}
              variant="contained"
            >
              {t('sendRequest')}
            </Button>
          ) : (
            <Box className="send" />
          )}
        </Box>
      </Box>
    </StyledGridSentenceButton>
  );
};

export default GridSentenceButton;
